import { observer } from "mobx-react-lite";
import { GetServerSideProps } from "next";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useRouter } from "next/router";
import React from "react";
import { useEffect, useRef, useState } from "react";

import Error404 from "../components/Error404";
import MainContainer from "../components/MainContainer";
import * as ArticleController from "../controllers/article-controller";
import * as AuthorController from "../controllers/author-controller";
import UserStore from "../store/UserStore";
import Article from "./[article]/Article";
import DraftArticle from "./[article]/DraftArticle";
import { RecommendedArticles } from "./[article]/RecommendedArticles";
import { RecommendedRandom } from "./[article]/RecommendedRandom";

interface IProps {
  article: Nullable<ArticleDetail>;
  author: Nullable<AuthorResponse>;
}

function ArticlePage({ article, author }: IProps) {
  const router = useRouter();
  const { t } = useTranslation();

  const [list, setList] = useState<IProps[]>([]);
  const [currentArticle, setCurrentArticle] = useState<ArticleDetail>();
  const count = useRef(0);
  const isScrollingRef = useRef(false);

  const { isAuth } = UserStore;

  const getNextArticle = async () => {
    const response = await ArticleController.getArticlesByFilter({
      filter: {
        tagslug: article?.tagslug,
        is_draft: false,
      },
      start: count.current,
      total: 1,
    });

    const newArticle = response?.list[0];
    if (newArticle?.article_id === article?.article_id) {
      count.current = count.current + 1;
      getNextArticle();
      return;
    }
    if (newArticle) {
      const article = await ArticleController.getArticleByIndex(
        newArticle.article_index
      );
      const author = await AuthorController.getAuthorById(article?.author_id);
      count.current = count.current + 1;
      setList((list) => [...list, { article, author }]);
    }
  };

  useEffect(() => {
    setList([]);
    count.current = 0;
    getNextArticle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [article]);

  useEffect(() => {
    if ("scrollRestoration" in history) {
      history.scrollRestoration = "manual";
      return () => {
        history.scrollRestoration = "auto";
      };
    }
  }, []);

  const handlePathChange = (article: ArticleDetail) => {
    router.push(
      {
        pathname: article?.article_index,
      },
      undefined,
      { shallow: true }
    );
    setCurrentArticle(article);
  };

  if (!article) {
    return !isAuth ? <Error404 /> : <DraftArticle />;
  }

  const { seo_title, seo_description, cover_image } = currentArticle || article;

  return (
    <MainContainer
      ogTitle={seo_title}
      ogDescription={seo_description}
      {...(cover_image && { ogImg: cover_image })}
    >
      <section className="article-page-section">
        <div className="wrap">
          <div className="main-columns article-page">
            <div className="main-columns__aside aside article-page__aside">
              <RecommendedRandom
                tagslug={article.tagslug}
                excludeId={article.article_id}
              />
            </div>
            <div className="main-columns__center article-page__center">
              <Article
                key={article.article_id}
                article={article}
                author={author}
                isScrollingRef={isScrollingRef}
                onPathChange={handlePathChange}
              />
            </div>
          </div>
        </div>
      </section>

      <RecommendedArticles
        tagslug={article.tagslug}
        excludeId={article.article_id}
      />

      {list.map(({ article, author }, key) => (
        <React.Fragment key={key}>
          <section className="article-page-section article-page-section-more">
            <div className="wrap">
              <a
                className="btn scale-up article-page-more"
                href="https://t.me/+WxrnVRWuPsdhMTgy"
                target="_blank"
                rel="noreferrer"
                style={{ width: "100%" }}
              >
                <span className="soc-icon soc-icon-Tg article-page-more__icon" />
                {t("MoreInTelegram")}
                <span className="icon icon-arrow-btn article-page-more__icon-arrow" />
              </a>
            </div>
          </section>
          <section className="article-page-section">
            <div className="wrap">
              <div className="main-columns article-page">
                <div className="main-columns__aside aside article-page__aside">
                  <RecommendedRandom
                    tagslug={article?.tagslug}
                    excludeId={article?.article_id}
                  />
                </div>
                <div className="main-columns__center article-page__center">
                  <Article
                    article={article}
                    author={author}
                    isScrollingRef={isScrollingRef}
                    onFinish={getNextArticle}
                    onPathChange={handlePathChange}
                  />
                </div>
              </div>
            </div>
          </section>
        </React.Fragment>
      ))}
      <RecommendedArticles
        tagslug={article.tagslug}
        excludeId={article.article_id}
      />
    </MainContainer>
  );
}

export default observer(ArticlePage);

export const getServerSideProps: GetServerSideProps = async ({
  params,
  res,
}): Promise<{ props: IProps }> => {
  const translationsProps = await serverSideTranslations("ru");

  const index = params?.article as string;

  const article = await ArticleController.getArticleByIndex(index);

  const author = await AuthorController.getAuthorById(article?.author_id);

  if (!article) {
    res.statusCode = 404;
  }

  return {
    props: {
      article,
      author,
      ...translationsProps,
    },
  };
};
